/**
 * The SynAnalytics privacy policy. Every statement here describes what the
 * code does; change the code and this page together. Google's review reads
 * the "Google user data" section against the Limited Use requirements.
 */

import type { ReactNode } from 'react';

export const PRIVACY_UPDATED = '23 October 2026';
const CONTACT = 'synteralabs.sa@gmail.com';
const GOOGLE_POLICY = 'https://developers.google.com/terms/api-services-user-data-policy';

export function PrivacyPolicy(): ReactNode {
  return (
    <>
      <p>
        SynAnalytics is a reporting service for marketing agencies, made by Syntera Labs in South
        Africa. This policy explains what we collect, why, where it is kept and the choices you
        have. We follow the Protection of Personal Information Act (POPIA).
      </p>

      <h2>What we collect</h2>
      <ul>
        <li>
          <strong>Your account:</strong> name, work email, organisation name and a password (stored
          only as a one-way hash). Two-step sign-in secrets are stored encrypted.
        </li>
        <li>
          <strong>What you put in:</strong> clients, dashboards, reports, comments, branding,
          schedules and the people you invite.
        </li>
        <li>
          <strong>Data from accounts you connect:</strong> numbers from the services you choose to
          connect, such as Google Analytics, Google Ads, Google Business Profile and SynLedger.
        </li>
        <li>
          <strong>Service records:</strong> sign-in times, security events and an audit trail of
          changes, kept to protect your account and to show who changed what.
        </li>
      </ul>

      <h2>Google user data</h2>
      <p>
        When you connect Google Analytics, you sign in with Google and approve read-only access (the{' '}
        <code>analytics.readonly</code> permission). With it, SynAnalytics:
      </p>
      <ul>
        <li>
          lists the Google Analytics properties your Google account can see, so you can choose which
          one reports for each client;
        </li>
        <li>
          reads report numbers from those properties (for example sessions, users, channels, pages,
          events and revenue) to show in the dashboards, reports, PDFs and share links you create;
        </li>
        <li>
          keeps those answers for at most six hours, so a reload or a PDF does not ask Google again,
          then deletes them;
        </li>
        <li>
          keeps the sign-in token Google issues encrypted, uses it only to read your reports, and
          never shows it to anyone, including you.
        </li>
      </ul>
      <p>
        We never change anything in your Google account, never sell Google data, never use it for
        advertising, never use it to train AI or machine-learning models, and never let people read
        it except where you ask us for support, where the law requires it, or where it is needed for
        security.
      </p>
      <p>
        SynAnalytics’ use and transfer to any other app of information received from Google APIs
        will adhere to the{' '}
        <a href={GOOGLE_POLICY} target="_blank" rel="noreferrer">
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements.
      </p>
      <p>
        <strong>Disconnecting:</strong> choose Disconnect on the Data page. We ask Google to cancel
        the access straight away and erase our copy of the token. You can also remove access at any
        time at{' '}
        <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer">
          myaccount.google.com/permissions
        </a>
        .
      </p>

      <h2>How we use information</h2>
      <p>
        Only to run SynAnalytics for you: to sign you in, show and send the reports you build, email
        you about your account (for example a PDF that is ready or a report waiting for approval),
        keep the service secure and answer your support requests. We do not sell personal
        information and do not show advertising.
      </p>

      <h2>Who processes it for us</h2>
      <ul>
        <li>
          <strong>Cloudflare</strong> hosts the app, its database and files, makes PDFs and checks
          sign-ins for bots.
        </li>
        <li>
          <strong>Resend</strong> delivers our emails.
        </li>
        <li>
          <strong>Have I Been Pwned</strong> checks new passwords against known breaches. Only the
          first five characters of a one-way hash are sent, never the password.
        </li>
        <li>
          The services you connect (Google, SynLedger and others) receive the requests needed to
          read your data from them.
        </li>
      </ul>
      <p>
        Some of these providers process data outside South Africa. We use them under agreements that
        protect personal information as POPIA requires.
      </p>

      <h2>How long we keep it</h2>
      <p>
        Account and report data stay while your account is open. Connected-service answers are kept
        only briefly, as described above. To close your account, email{' '}
        <a href={`mailto:${CONTACT}`}>{CONTACT}</a> from the account owner’s address; we then delete
        your information, except records the law requires us to keep.
      </p>

      <h2>Security</h2>
      <p>
        Traffic is encrypted, secrets from connected services are encrypted at rest, sign-in uses
        strong passwords with two-step verification, and every change is logged.
      </p>

      <h2>Your rights</h2>
      <p>
        You may ask to see, correct or delete your personal information, object to how it is used,
        or complain to the Information Regulator of South Africa. Write to us at{' '}
        <a href={`mailto:${CONTACT}`}>{CONTACT}</a>; our Information Officer will answer.
      </p>

      <h2>Changes</h2>
      <p>
        If we change this policy we will update the date above, and tell account owners by email
        before a change that matters takes effect.
      </p>
    </>
  );
}
